/**
 * Shared formatters used across the admin widgets (overview / cost
 * tracking / platform settings). The existing v2 admin components
 * (Quality / Engagement / Cost / Users) each ship their own inline
 * ``formatInt`` / ``formatPct`` / ``formatUsd`` because they predate
 * this module; new code goes through this single source of truth so a
 * change to currency / locale lands once.
 *
 * Italian locale is the project default (the rest of v2 already uses
 * ``"it-IT"`` everywhere for ``toLocaleString``).
 */

export function formatInt(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return "—";
  return Math.round(n).toLocaleString("it-IT");
}

export function formatPct(n: number | null | undefined, digits = 1): string {
  if (n == null || !Number.isFinite(n)) return "—";
  return `${n.toFixed(digits)}%`;
}

export function formatUsd(n: number | null | undefined, digits = 4): string {
  if (n == null || !Number.isFinite(n)) return "—";
  return `$${n.toFixed(digits)}`;
}

export function formatMs(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return "—";
  if (n < 1000) return `${Math.round(n)}ms`;
  if (n < 60_000) return `${(n / 1000).toFixed(1)}s`;
  const min = Math.floor(n / 60_000);
  const sec = Math.round((n % 60_000) / 1000);
  return `${min}m ${sec}s`;
}

/**
 * Generic KPI formatter used by chart axes — picks the right shape
 * based on the unit token (``"$"`` / ``"%"`` / ``"ms"`` / otherwise
 * integer-ish).
 */
export function formatKpi(value: number, unit: string): string {
  if (unit === "$") return `$${value.toFixed(2)}`;
  if (unit === "%") return `${value.toFixed(1)}%`;
  if (unit === "ms") return formatMs(value);
  if (Number.isInteger(value) || Math.abs(value) >= 1000) {
    return Math.round(value).toLocaleString("it-IT");
  }
  return value.toFixed(2);
}

/** Short date "5 mag" — used for chart x-axis labels. */
export function formatDateShort(iso: string | null | undefined): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("it-IT", {
      day: "numeric",
      month: "short",
    });
  } catch {
    return iso;
  }
}

/** Compact timestamp "12/05 14:32" — used by trace tables. */
export function formatTimestamp(iso: string | null | undefined): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

/** Time-only "14:32:05" — used by trace detail modal waterfall. */
export function formatHHMMSS(iso: string | null | undefined): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString("it-IT", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch {
    return iso;
  }
}
